/**
 * Outbound message signature + link resolution.
 *
 * Anything a user composes in their OWN email / SMS / WhatsApp client from
 * inside Cannafax gets a short Cannafax line appended. This module is the
 * single source of that wording AND of the link it carries.
 *
 * The hard rule (see plan "Where the link can safely point"): only four routes
 * in the app render for someone without an account —
 *   /s/<slug>  /share/<id>  /shared/<id>  /menu-invite/<token>
 * Everything else sits behind auth AND behind company/menu access. Linking a
 * recipient to an auth-only path produces a login wall or an access-denied
 * page, which is a DEAD trigger and worse than no link at all.
 *
 * So the destination is resolved by evidence, never by where the sender
 * happens to be standing:
 *   tier 1  an already-live public share link exists  -> use it
 *   tier 2  the recipient is provably inside          -> in-app deep link
 *   tier 3  everything else                           -> the homepage
 * Default is tier 3. A deep link requires positive evidence.
 */

export type OutboundChannel = 'email' | 'sms' | 'whatsapp';

export const CANNAFAX_HOME_URL = 'https://cannafax.ai';

/** Tagline variants. `default` is what ships; the rest are here so the copy
 *  decision is one edit, not a code hunt. */
export const OUTBOUND_TAGLINES = {
  default: 'my live inventory lives here',
  utility: 'everything I have, always current',
  insider: 'I keep my book on Cannafax',
  curiosity: "see what I'm holding this week",
} as const;

const TAGLINE = OUTBOUND_TAGLINES.default;

/** Public route prefixes — the only destinations a signed-out recipient can open. */
const PUBLIC_PREFIXES = ['/s/', '/share/', '/shared/', '/menu-invite/'];

export const isPubliclyReachablePath = (path: string): boolean => {
  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path);
      return PUBLIC_PREFIXES.some((p) => u.pathname.startsWith(p));
    } catch {
      return false;
    }
  }
  return PUBLIC_PREFIXES.some((p) => path.startsWith(p));
};

export interface OutboundLinkEvidence {
  /** Tier 1: an ALREADY-EXISTING public share URL or path for the thing being
   *  discussed. Never mint one as a side effect of sending a message. */
  publicShareUrl?: string | null;
  /** Tier 2: in-app destination, only used when `recipientHasAccess` is true. */
  inAppPath?: string | null;
  /** Positive proof the recipient can open `inAppPath` (teammate, or a buyer
   *  already subscribed to the menu in question). Defaults to false. */
  recipientHasAccess?: boolean;
}

export interface ResolvedOutboundLink {
  url: string;
  tier: 1 | 2 | 3;
}

const absolute = (pathOrUrl: string): string =>
  /^https?:\/\//i.test(pathOrUrl)
    ? pathOrUrl
    : `${CANNAFAX_HOME_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;

export function resolveOutboundLink(evidence?: OutboundLinkEvidence | null): ResolvedOutboundLink {
  const share = evidence?.publicShareUrl?.trim();
  if (share && isPubliclyReachablePath(share)) {
    return { url: absolute(share), tier: 1 };
  }

  const inApp = evidence?.inAppPath?.trim();
  if (inApp && evidence?.recipientHasAccess === true) {
    return { url: absolute(inApp), tier: 2 };
  }

  return { url: CANNAFAX_HOME_URL, tier: 3 };
}

const displayUrl = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

/**
 * The signature text for a channel. Email gets a separator + one line; SMS and
 * WhatsApp collapse to the shortest possible form because characters are billed
 * and long footers get messages split or filtered.
 */
export function outboundSignature(
  channel: OutboundChannel,
  evidence?: OutboundLinkEvidence | null,
): string {
  const { url } = resolveOutboundLink(evidence);
  const short = displayUrl(url);

  if (channel === 'email') {
    return `--\nSent from Cannafax — ${TAGLINE}.\n${short}`;
  }
  // SMS / WhatsApp: one short line, no separator art.
  return `— via Cannafax, ${TAGLINE}: ${short}`;
}

export interface AppendOptions {
  /** Skip the signature entirely (e.g. messaging your own teammate). */
  skip?: boolean;
  evidence?: OutboundLinkEvidence | null;
}

/** Max pre-filled body length we will hand to a mail/SMS client. Well inside
 *  what clients accept, so a body is never truncated or silently dropped. */
export const MAX_PREFILL_LENGTH = 1200;

/**
 * Compose a pre-filled message body: the contextual intro the user would have
 * typed anyway, then blank space to type in, then the signature.
 */
export function appendSignature(
  channel: OutboundChannel,
  body: string,
  options: AppendOptions = {},
): string {
  const intro = (body ?? '').trim();
  if (options.skip) return intro;

  const sig = outboundSignature(channel, options.evidence);
  const joiner = channel === 'email' ? '\n\n\n' : '\n\n';
  const composed = intro ? `${intro}${joiner}${sig}` : sig;

  return composed.length > MAX_PREFILL_LENGTH
    ? `${intro.slice(0, Math.max(0, MAX_PREFILL_LENGTH - sig.length - joiner.length))}${joiner}${sig}`
    : composed;
}
